import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getInterestsForState } from "@/api/interests";
import { Link } from "react-router-dom";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import { InputField, SelectField } from "@/components/ui/FormFields";
import {
  searchInterests,
  proposeInterest,
} from "@/api/interests";
import type { InterestType, ProposeInterestResponse, ApiResponse } from "@/api/types";

// Interest types per backend enum. The propose flow targets the
// Phase 13.6 / 1A additions plus a handful of pre-existing types
// users commonly want to extend.
const INTEREST_TYPES: { value: InterestType; label: string }[] = [
  { value: "touchstone_film", label: "Touchstone — Film" },
  { value: "touchstone_tv", label: "Touchstone — TV" },
  { value: "touchstone_music", label: "Touchstone — Music" },
  { value: "touchstone_cultural", label: "Touchstone — Cultural" },
  { value: "spiritual_practice", label: "Spiritual practice" },
  { value: "sewa_type", label: "Sewa type" },
  { value: "hobby_dance", label: "Hobby — Dance" },
  { value: "hobby_art", label: "Hobby — Art" },
  { value: "hobby_social", label: "Hobby — Social" },
  { value: "sport_modern", label: "Sport — Modern" },
  { value: "sport_traditional", label: "Sport — Traditional" },
];

export default function InterestsLab() {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");

  // Tiny debounce so we don't search on every keystroke.
  useDebounce(() => setDebounced(query), 250, [query]);

  const { data: searchResult, isFetching } = useQuery({
    queryKey: ["interests-search", debounced],
    queryFn: () => searchInterests({ q: debounced, limit: 20 }),
    enabled: debounced.length > 0,
  });

  return (
    <div className="space-y-8">
      <PageHeader
        title="Custom Interests"
        subtitle="Search the catalog. If nothing matches, propose a new one."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Custom Interests" },
        ]}
        actions={
          <Link to="/lab/interests/my-proposals">
            <Button variant="secondary" size="sm">
              My proposals
            </Button>
          </Link>
        }
      />

      <StateRound />

      {/* Search */}
      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <InputField
          label="Search the catalog"
          placeholder="Try 'cricket', 'satsang', 'doordarshan'…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          hint={`Search hits ${debounced ? `the API for "${debounced}"` : "as you type"}. Use aliases too.`}
        />
        {debounced.length > 0 && (
          <div className="mt-4">
            {isFetching && (
              <p className="text-sm text-gray-500">Searching…</p>
            )}
            {!isFetching && searchResult && (
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  {searchResult.data.count} match
                  {searchResult.data.count === 1 ? "" : "es"} for{" "}
                  <span className="font-medium">&ldquo;{searchResult.data.query}&rdquo;</span>
                </p>
                {searchResult.data.count === 0 ? (
                  <p className="text-sm text-gray-500">
                    No matches. Try the propose form below to add this to the catalog.
                  </p>
                ) : (
                  <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
                    {searchResult.data.items.map((interest) => (
                      <li
                        key={interest.id}
                        className="px-4 py-3 flex items-center justify-between"
                      >
                        <div>
                          <div className="font-medium text-gray-900">
                            {interest.emoji ? `${interest.emoji} ` : ""}
                            {interest.canonicalNameEn}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            <code className="font-mono">{interest.code}</code> ·{" "}
                            {interest.interestType} · {interest.category}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      <ProposeForm />
    </div>
  );
}

/**
 * One state's round, as the Identity Board offers it: every item popular in the
 * state plus the pan-India ones, with one decade's items first.
 */
function StateRound() {
  const [stateCode, setStateCode] = useState("GJ");
  const [decade, setDecade] = useState("90s");
  const { data, isFetching } = useQuery({
    queryKey: ["interests-for-state", stateCode, decade],
    queryFn: () => getInterestsForState(stateCode, decade || undefined),
    enabled: stateCode.length === 2,
  });
  const round = data?.data;
  const categories = ["music", "entertainment", "hobbies"];
  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
      <div>
        <h2 className="text-sm font-medium text-gray-900">A state's round</h2>
        <p className="text-xs text-gray-500">
          What the Identity Board offers a member from this state. A round should hold at least
          twelve per category, and the chosen decade comes first.
        </p>
      </div>
      <div className="flex gap-2 flex-wrap items-center">
        <input
          className="w-20 px-3 py-2 border border-gray-300 rounded-md text-sm font-mono uppercase"
          value={stateCode}
          maxLength={2}
          onChange={(e) => setStateCode(e.target.value.toUpperCase())}
          placeholder="GJ"
        />
        <select
          className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          value={decade}
          onChange={(e) => setDecade(e.target.value)}
        >
          <option value="">no decade</option>
          {["60s", "70s", "80s", "90s", "2000s", "2010s"].map((d) => (
            <option key={d} value={d}>{d} first</option>
          ))}
        </select>
        {isFetching && <span className="text-xs text-gray-400">loading…</span>}
        {round && (
          <span className="text-xs text-gray-500">
            {round.count} items · decade first: {round.decade ?? "none"}
          </span>
        )}
      </div>
      {round && (
        <div className="grid md:grid-cols-3 gap-3">
          {categories.map((category) => {
            const items = round.byCategory[category] ?? [];
            return (
              <div key={category} className="border border-gray-200 rounded-xl p-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 capitalize">{category}</span>
                  <Badge variant={items.length >= 12 ? "green" : "yellow"}>{items.length}</Badge>
                </div>
                <ul className="text-xs text-gray-600 mt-1 space-y-0.5">
                  {items.slice(0, 8).map((item) => (
                    <li key={item.code}>
                      {item.emoji} {item.canonicalNameEn}
                      {item.isPanIndia ? <span className="text-gray-400"> · pan-India</span> : null}
                    </li>
                  ))}
                  {items.length > 8 && <li className="text-gray-400">… {items.length - 8} more</li>}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function ProposeForm() {
  const [type, setType] = useState<InterestType>("touchstone_film");
  const [label, setLabel] = useState("");
  const [emoji, setEmoji] = useState("");
  const [categoryHint, setCategoryHint] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ProposeInterestResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const response = (await proposeInterest({
        type,
        label,
        emoji: emoji || undefined,
        categoryHint: categoryHint || undefined,
      })) as ApiResponse<ProposeInterestResponse>;
      setResult(response.data);
      setLabel("");
      setEmoji("");
      setCategoryHint("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to propose interest");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">
        Propose a new interest
      </h2>
      <p className="text-sm text-gray-500 mb-4">
        Auto-approves on the 3rd independent submission of the same value (D2);
        otherwise goes to the admin queue.
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <SelectField
          label="Interest type"
          required
          value={type}
          onChange={(e) => setType(e.target.value as InterestType)}
          options={INTEREST_TYPES}
        />
        <InputField
          label="Label"
          placeholder="e.g. Doordarshan Mahabharat"
          required
          maxLength={100}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <InputField
          label="Emoji (optional)"
          placeholder="📺"
          maxLength={4}
          value={emoji}
          onChange={(e) => setEmoji(e.target.value)}
        />
        <InputField
          label="Category hint (optional)"
          placeholder="e.g. nostalgic_tv"
          maxLength={50}
          value={categoryHint}
          onChange={(e) => setCategoryHint(e.target.value)}
        />
        <Button type="submit" isLoading={submitting} disabled={!label.trim()}>
          Submit proposal
        </Button>
      </form>

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
          {error}
        </div>
      )}
      {result && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded text-sm">
          <div className="font-medium text-green-900 flex items-center gap-2">
            <Badge variant="green">{result.status}</Badge>
            {result.message ?? "Proposal submitted"}
          </div>
          <pre className="mt-2 text-xs text-green-800 whitespace-pre-wrap font-mono">
            {JSON.stringify(result.interest, null, 2)}
          </pre>
        </div>
      )}
    </section>
  );
}

// ─── Tiny debounce hook (avoids an extra dep) ──────────────────────
import { useEffect } from "react";
function useDebounce(fn: () => void, ms: number, deps: unknown[]): void {
  useEffect(() => {
    const t = setTimeout(fn, ms);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
